<template>
  <section id="report-body-information">
    <!-- Top Heading Block -->
    <div class="top-heading-block">
      <div class="patient-information-block">
        <table class="patient-details w-100">
          <tr>
            <td style="width: 33.33%">
              <p>
                <span class="line-name">Patient </span><span class="line-value">{{ patient.name }}</span>
              </p>
              <p>
                <span class="line-name">Date of Birth</span><span class="line-value">
                  {{ formatDate(patient.dob) }}
                </span>
              </p>
              <p>
                <span class="line-name">Sex</span><span class="line-value">{{ patient.sex }}</span>
              </p>
              <p>
                <span class="line-name">Phone</span><span class="line-value">{{ patient.phone }}</span>
              </p>
            </td>
            <td style="width: 33.33%">
              <p>
                <span class="line-name">Physician </span><span class="line-value">{{
                  patientDemographics.physician
                  }}</span>
              </p>
              <p>
                <span class="line-name">NPI #</span><span class="line-value">{{ patientDemographics.npi }}</span>
              </p>
              <p>
                <span class="line-name">Practice Care</span><span class="line-value">{{
                  patientDemographics.practice_branch ||
                  patientDemographics.practice
                  }}</span>
              </p>
              <p>
                <span class="line-name">Phone</span><span class="line-value">{{
                  patientDemographics.physicianPhone
                  }}</span>
              </p>
            </td>
            <td style="width: 33.33%">
              <p>
                <span class="line-name">Date Collected</span><span class="line-value">
                  {{
                    momentWithTimezone(
                      patientDemographics.collected,
                      "MMM DD, yyyy, HH:mm"
                    )
                  }}
                </span>
              </p>
              <p>
                <span class="line-name">Date Received</span><span class="line-value">
                  {{
                    momentWithTimezone(
                      patientDemographics.received,
                      "MMM DD, yyyy, HH:mm"
                    )
                  }}
                </span>
              </p>
              <p>
                <span class="line-name">Date Processed</span><span class="line-value">
                  {{
                    momentWithTimezone(
                      patientDemographics.reported,
                      "MMM DD, yyyy"
                    )
                  }}
                </span>
              </p>
              <p>
                <span class="line-name">
                  Specimen type
                  <br />/Source </span><span class="line-value">
                  {{ patientDemographics.specimen }}
                </span>
              </p>
              <p>
                <span class="line-name">Sample ID</span><span class="line-value">
                  {{ patientDemographics.sampleName }}
                </span>
              </p>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <section class="screen-validity">
      <div class="medications-table mt-20">
        <div class="w-100">
          <h4>Medications</h4>
          <p class="mt-0 pl-10">
            {{ medications }}
          </p>
        </div>
      </div>
    </section>
    <!-- Confirmation Positive/Inconsistent Summary  -->
    <section id="confirmation-results" v-if="confirmationSummary.length">
      <div class="confirmation-table-heading type-2">
        <h4>Confirmation Positive / Inconsistent Summary</h4>
        <div class="table-header">
          <div class="each-result">
            <table class="w-100">
              <tbody>
                <tr class="table-header-for-values">
                  <td>Test</td>
                  <td style="width: 120px">Result (ng/mL)</td>
                  <td class="table-hd" style="width: 200px">Interpretation</td>
                  <td class="table-hd" style="width: 100px">Flag</td>
                  <td class="table-hd text-right" style="width: 100px">
                    Cutoff (ng/mL)
                  </td>
                  <td style="width: 30px"></td>
                  <td style="width: 110px">Comments</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="type-2" v-for="(category, index) of categoryCodes2" :key="index">
        <div class="each-result" v-if="category.name">
          <h5 class="table-heading">{{ category.name }}</h5>
          <table class="w-100">
            <tbody>
              <tr v-for="(result, cindex) of category.results" :key="cindex">
                <td>{{ result.report_test_name }}</td>
                <td style="width: 120px">{{ getResultText(result) }}</td>
                <td style="width: 200px" :class="result.interpretation == 'INCONSISTENT' ? 'red' : ''">
                  {{ result.interpretation }}
                </td>
                <td style="width: 100px" :class="result.flag &&
                    result.flag.toLowerCase().trim() == 'positive'
                    ? 'red'
                    : ''
                  ">
                  {{ result.flag || "" }}
                </td>
                <td style="width: 100px" class="text-right">
                  {{ result.cutoff || "" }}
                </td>
                <td style="width: 30px"></td>
                <td style="width: 110px">{{ result.comment || "" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    <!-- Confirmation results -->
    <section id="confirmation-results" v-if="confirmationResults.length">
      <div class="confirmation-table-heading">
        <h4 class="">Confirmation Results</h4>
        <div class="table-header">
          <div class="each-result">
            <table class="w-100">
              <tbody>
                <tr>
                  <td style="width: 150px">Compound</td>
                  <td style="width: 100px" class="text-right">
                    Result (ng/mL)
                  </td>
                  <td style="width: 200px" class="table-hd">Interpretation</td>
                  <td style="width: 80px" class="table-hd">Flag</td>
                  <td style="width: 100px" class="table-hd text-right">
                    Cutoff (ng/mL)
                  </td>
                  <td style="width: 10px"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-for="(category, index) of categoryCodes" :key="index">
        <div class="each-result" v-if="category.name">
          <h5 class="table-heading">{{ category.name }}</h5>
          <table class="w-100">
            <tbody>
              <tr v-for="(result, cindex) of category.results" :key="cindex">
                <td style="width: 150px">{{ result.report_test_name }}</td>
                <td style="width: 100px" class="text-right">
                  {{ getResultText(result) }}
                </td>
                <td style="width: 200px" :class="result.interpretation == 'INCONSISTENT' ? 'red' : ''">
                  {{ result.interpretation }}
                </td>
                <td style="width: 80px" :class="result.flag &&
                    result.flag.toLowerCase().trim() == 'positive'
                    ? 'red'
                    : ''
                  ">
                  {{ result.flag || "" }}
                </td>
                <td style="width: 100px" class="text-right">
                  {{ result.cutoff || "" }}
                </td>
                <td style="width: 10px"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- ETG_ETS results -->
    <section id="confirmation-results" v-if="etsetsResults.length">
      <div class="confirmation-table-heading">
        <h4 class="">ETG_ETS Results</h4>
        <div class="table-header">
          <div class="each-result">
            <table class="w-100">
              <tbody>
                <tr>
                  <td style="width: 150px">Compound</td>
                  <td style="width: 100px" class="text-right">
                    Result (ng/mL)
                  </td>
                  <td style="width: 200px" class="table-hd">Interpretation</td>
                  <td style="width: 80px" class="table-hd">Flag</td>
                  <td style="width: 100px" class="table-hd text-right">
                    Cutoff (ng/mL)
                  </td>
                  <td style="width: 10px"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-for="(category, index) of etgetsCodes" :key="index">
        <div class="each-result" v-if="category.name">
          <h5 class="table-heading">{{ category.name }}</h5>
          <table class="w-100">
            <tbody>
              <tr v-for="(result, cindex) of category.results" :key="cindex">
                <td style="width: 150px">{{ result.report_test_name }}</td>
                <td style="width: 100px" class="text-right">
                  {{ getResultText(result) }}
                </td>
                <td style="width: 200px" :class="result.interpretation == 'INCONSISTENT' ? 'red' : ''">
                  {{ result.interpretation }}
                </td>
                <td style="width: 80px" :class="result.flag &&
                    result.flag.toLowerCase().trim() == 'positive'
                    ? 'red'
                    : ''
                  ">
                  {{ result.flag || "" }}
                </td>
                <td style="width: 100px" class="text-right">
                  {{ result.cutoff || "" }}
                </td>
                <td style="width: 10px"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section id="confirmation-results" class="screen-validity next-page" v-if="screenValidity && screenValidity.length">
      <div class="confirmation-table-heading">
        <div class="table-header">
          <div class="each-result">
            <h4>Screen Validity</h4>
            <table class="w-100">
              <tbody>
                <tr>
                  <td style="width: 150px">Test Name</td>
                  <td style="width: 100px" class="text-right">Result</td>
                  <td style="width: 200px">Interpretation</td>
                  <td style="width: 80px">Flag</td>
                  <td style="width: 100px" class="text-right">
                    Cutoff (ng/mL)
                  </td>
                  <td style="width: 10px"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="each-result">
        <table class="w-100">
          <tbody>
            <tr v-for="(test, index) of screenValidity" :key="index">
              <td style="width: 150px">{{ test.report_test_name }}</td>
              <td style="width: 100px" class="text-right" :class="test['result_value'] == 'Abnormal' ? 'red' : ''">
                {{ test["result_value"] }}
              </td>
              <td style="width: 200px"></td>
              <td style="width: 80px"></td>
              <td style="width: 100px" class="text-right">
                {{ test.cutoff }}
              </td>
              <td style="width: 10px"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="screen-validity" id="confirmation-results" v-if="screenResults && screenResults.length">
      <div class="confirmation-table-heading">
        <div class="table-header">
          <div class="each-result">
            <h4>Screen Results</h4>
            <table class="w-100">
              <tbody>
                <tr>
                  <td style="width: 150px">Test Name</td>
                  <td style="width: 100px" class="text-right">Result</td>
                  <td style="width: 200px">Interpretation</td>
                  <td style="width: 80px">Flag</td>
                  <td style="width: 100px" class="text-right">
                    Cutoff (ng/mL)
                  </td>
                  <td style="width: 10px"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="each-result">
        <table class="w-100">
          <tbody>
            <tr v-for="(test, index) of screenResults" :key="index">
              <td style="width: 150px">{{ test.report_test_name }}</td>
              <td style="width: 100px" class="text-right" :class="test['result_value'] === 'Positive' ||
                  test['result_value'] === 'Not Accepted'
                  ? 'red'
                  : ''
                ">
                {{ test["result_value"] }}
              </td>
              <td style="width: 200px"></td>
              <td style="width: 80px"></td>
              <td style="width: 100px" class="text-right">
                {{ test.cutoff }}
              </td>
              <td style="width: 10px"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <br />
    <div class="description next-page">
      <p v-if="reportsData && reportsData.lab_details">
        The confirmatory methods were developed and their performance
        characteristics were determined by
        {{ reportsData.lab_details ? reportsData.lab_details.name : "" }}. The
        methods have not been cleared or approved by FDA. The laboratory is
        regulated under CLIA as qualifid to perform high- complexity testing.
        These tests are used for clinic purposes. It should not be regarded as
        investigational or research.
      </p>
      <br />

      <p v-if="isFinalized">
        Final Report Electronically signed by {{ signedBy }}
      </p>

      <p v-if="isTestPerformedBy">Test Performed by {{ testPerformedBy }}</p>
      <br />

      <p>
        Laboratory Certification:
        <span>CLIA # {{ CLIA }}</span>
      </p>
      <br />
      <p>
        Laboratory Director:
        <span>{{ labDirector }}</span>
      </p>
      <br />
    </div>
  </section>
</template>

<script>
import moment from "moment";

export default {
  props: ["reportsData"],
  data() {
    return {
      patient: {
        name: "-",
        sex: "-",
        dob: "-",
      },
      patientDemographics: {},
      dateFormat: "MMM DD, YYYY, HH:mm",
      isPositive: false,
      rcControl: false,
      summary: {
        N_gene: {},
        S_gene: {},
        ORF1ab: {},
        "RNase P Control": {},
      },
      summaryValue: "",
      summaryText: "",
      resultAssays: [],
      listA: [],
      listB: [],
      antibioticResistanceAssays: [],
      antibioticResistanceAssaysListA: [],
      antibioticResistanceAssaysListB: [],
      allResultAssaysA: [],
      allResultAssaysB: [],
      labDetails: {},
      confirmationResults: [],
      etsetsResults: [],
      confirmationSummary: [],
      screenValidity: [],
      screenResults: [],
      etgetsCodes: [],
      categoryCodes: [],
      categoryCodes2: [],
      medications: "",
      CLIA: "--",
      labDirector: "--",
      labName: "",
      signedBy: "",
      reportedDate: "",
      isFinalized: false,
      isTestPerformedBy: false,
      testPerformedBy: "",
    };
  },
  mounted() {
    if (this.reportsData) {
      this.setData();
    }
  },
  methods: {
    setData() {
      this.patientDemographics = this.reportsData.patient_demographics;
      this.confirmationResults =
        this.reportsData.result_summary.confirmation_results || [];
      this.etsetsResults =
        this.reportsData.result_summary.ETG / ETS_results || [];
      this.confirmationSummary =
        this.reportsData.result_summary.confirmation_summary || [];
      this.screenValidity =
        this.reportsData.result_summary.screen_validity || [];
      this.screenResults = this.reportsData.result_summary.screen_results || [];

      this.confirmationResults = this.confirmationResults.sort(function (a, b) {
        return ("" + a.category_code).localeCompare(b.category_code);
      });
      this.etsetsResults = this.etsetsResults.sort(function (a, b) {
        return ("" + a.category_code).localeCompare(b.category_code);
      });

      this.confirmationSummary = this.confirmationSummary.sort(function (a, b) {
        return ("" + a.category_code).localeCompare(b.category_code);
      });

      this.screenResults = this.screenResults.sort(function (a, b) {
        return ("" + a.report_test_name).localeCompare(b.report_test_name);
      });

      this.screenValidity = this.screenValidity.sort(function (a, b) {
        return ("" + a.report_test_name).localeCompare(b.report_test_name);
      });

      this.medications =
        this.reportsData &&
          this.reportsData.result_summary &&
          this.reportsData.result_summary.prescribed_medications &&
          this.reportsData.result_summary.prescribed_medications.length
          ? this.reportsData.result_summary.prescribed_medications.join(",  ")
          : "- No Medications -";
      if (this.patientDemographics.middlename) {
        this.patient.name =
          this.patientDemographics.givenName +
          ", " +
          this.patientDemographics.middlename +
          ", " +
          this.patientDemographics.sn;
      } else {
        this.patient.name =
          this.patientDemographics.givenName +
          ", " +
          this.patientDemographics.sn;
      }
      this.patient.dob = moment(this.patientDemographics.dob).format(
        this.dateFormat
      );

      if (this.reportsData && this.reportsData.lab_details) {
        if (this.reportsData.lab_details.clia_id) {
          this.CLIA = this.reportsData.lab_details.clia_id;
        }
        if (this.reportsData.lab_details.director) {
          this.labDirector = this.reportsData.lab_details.director;
        }
        if (this.reportsData.lab_details.name) {
          this.labName = this.reportsData.lab_details.name;
        }
      }

      this.reportedDate = this.reportsData.created_at;
      this.isFinalized = this.reportsData.is_finalized;
      if (this.reportsData.test_performed_by) {
        this.isTestPerformedBy = true;
        this.testPerformedBy = this.reportsData.test_performed_by;
      }
      if (this.isFinalized) {
        this.signedBy = this.reportsData.finalized_by;
        this.reportedDate = this.reportsData.finalized_at;
      }

      this.patient.sex = this.patientDemographics.sex;
      this.patient.phone = this.patientDemographics.phone;

      // set lab details
      this.labDetails = this.reportsData.lab_details;

      this.reportedDate = this.reportsData.created_at;
      this.isFinalized = this.reportsData.is_finalized;

      if (this.isFinalized) {
        this.signedBy = this.reportsData.finalized_by;
        this.reportedDate = this.reportsData.finalized_at;
      }
      this.categoryCodes = this.confirmationResults.map(
        (result) => result.category
      );
      // removed duplicates
      this.categoryCodes = [...new Set(this.categoryCodes)];

      this.categoryCodes = this.categoryCodes.map((categoryCode) => {
        return {
          name: categoryCode,
          results: this.getCategoryCodeData(
            categoryCode,
            this.confirmationResults
          ),
        };
      });
      this.etgetsCodes = this.etsetsResults.map((result) => result.category);
      // removed duplicates
      this.etgetsCodes = [...new Set(this.etgetsCodes)];

      this.etgetsCodes = this.etgetsCodes.map((categoryCode) => {
        return {
          name: categoryCode,
          results: this.getCategoryCodeData(categoryCode, this.etsetsResults),
        };
      });

      this.categoryCodes2 = this.confirmationSummary.map(
        (result) => result.category
      );
      // removed duplicates
      this.categoryCodes2 = [...new Set(this.categoryCodes2)];

      this.categoryCodes2 = this.categoryCodes2.map((categoryCode) => {
        return {
          name: categoryCode,
          results: this.getCategoryCodeData(
            categoryCode,
            this.confirmationSummary
          ),
        };
      });
    },
    getCategoryCodeData(category, data) {
      return data
        .filter((result) => {
          if (result.category == category) {
            return result;
          }
        })
        .sort(function (a, b) {
          return ("" + a.medication).localeCompare(b.medication);
        });
    },

    getResultText(test) {
      let value = test.result ? parseFloat(test.result) : undefined;

      if (value && !isNaN(value)) {
        if (value > parseFloat(test.ULMI)) {
          return "> " + test.ULMI;
        } else if (value < parseFloat(test.LLMI)) {
          return "< " + test.LLMI;
        } else {
          return value;
        }
      } else {
        return "----";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-header tr td {
  font-weight: 600;
}
</style>
